import React, { useEffect, useRef } from 'react';
import { Check, CircleDot } from 'lucide-react';

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  stepLabels?: string[];
  onStepClick?: (step: number) => void;
}

const DEFAULT_STEP_LABELS = ['Select Institute', 'Agent Information', 'Submit Business Licence', 'Terms and Conditions'];

interface StepData {
  stepNumber: number;
  isCompleted: boolean;
  isCurrent: boolean;
  stepLabel: string;
  isClickable: boolean;
}

export const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, totalSteps, stepLabels = DEFAULT_STEP_LABELS, onStepClick }) => {
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Scroll current step into view when it changes
  useEffect(() => {
    const currentStepElement = stepRefs.current[currentStep];
    if (!currentStepElement) return;

    const scrollContainer = window.innerWidth >= 992 
      ? desktopScrollRef.current 
      : mobileScrollRef.current;

    if (!scrollContainer) return;

    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = currentStepElement.getBoundingClientRect();

      // Calculate scroll position to center the current step
      const elementCenter = elementRect.left + elementRect.width / 2;
      const containerCenter = containerRect.left + containerRect.width / 2;
      const scrollOffset = elementCenter - containerCenter;

      // Only scroll if element is not centered or out of view
      const isOutOfView = 
        elementRect.left < containerRect.left ||
        elementRect.right > containerRect.right;
      
      const isNotCentered = Math.abs(scrollOffset) > 50; // 50px threshold

      if (isOutOfView || isNotCentered) {
        scrollContainer.scrollBy({
          left: scrollOffset,
          behavior: 'smooth',
        });
      }
    }, 100);
  }, [currentStep]);

  const handleStepClick = (stepNumber: number) => {
    if (onStepClick && stepNumber !== currentStep) {
      onStepClick(stepNumber);
    }
  };

  const getStepData = (index: number): StepData => {
    const stepNumber = index + 1;
    return {
      stepNumber,
      isCompleted: stepNumber < currentStep,
      isCurrent: stepNumber === currentStep,
      stepLabel: stepLabels[index] || `Step ${stepNumber}`,
      isClickable: !!onStepClick && stepNumber !== currentStep,
    };
  };

  const renderStepCircle = (step: StepData) => {
    const baseSize = 35;
    const currentStepSize = step.isCurrent ? 34 : baseSize;
    const ringSize = 41;
    
    return (
      <div
        className="position-relative d-flex align-items-center justify-content-center"
        style={{
          width: step.isCurrent ? `${ringSize}px` : `${baseSize}px`,
          height: `${ringSize}px`,
        }}
      >
        {/* Outer ring for current step - enhanced visibility */}
        {step.isCurrent && (
          <div
            className="position-absolute rounded-circle"
            style={{
              width: `${ringSize}px`,
              height: `${ringSize}px`,
              backgroundColor: 'rgba(13, 110, 253, 0.1)',
              border: '2px solid rgba(13, 110, 253, 0.3)',
              animation: 'pulse 5s ease-in-out infinite',
            }}
          />
        )}
        
        {/* Main step circle */}
        <div
          className={`rounded-circle d-flex align-items-center justify-content-center position-relative ${
            step.isCompleted
              ? 'bg-success text-white'
              : step.isCurrent
              ? 'bg-primary text-white'
              : 'bg-light text-muted border'
          } ${step.isClickable ? 'cursor-pointer' : ''}`}
          style={{
            width: `${currentStepSize}px`,
            height: `${currentStepSize}px`,
            fontSize: step.isCurrent ? '14px' : '12px',
            fontWeight: step.isCurrent ? '700' : '600',
            transition: 'all 0.3s ease',
            cursor: step.isClickable ? 'pointer' : 'default',
            boxShadow: step.isCurrent 
              ? '0 4px 12px rgba(13, 110, 253, 0.4), 0 0 0 3px rgba(13, 110, 253, 0.1)' 
              : 'none',
            transform: step.isCurrent ? 'scale(1.1)' : 'scale(1)',
            zIndex: 1,
          }}
          onClick={() => handleStepClick(step.stepNumber)}
          role={step.isClickable ? 'button' : undefined}
          tabIndex={step.isClickable ? 0 : undefined}
          onKeyDown={(e) => {
            if (step.isClickable && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleStepClick(step.stepNumber);
            }
          }}
          onMouseEnter={(e) => {
            if (step.isClickable) {
              e.currentTarget.style.transform = 'scale(1.15)';
              e.currentTarget.style.opacity = '0.9';
            }
          }}
          onMouseLeave={(e) => {
            if (step.isClickable) {
              e.currentTarget.style.transform = step.isCurrent ? 'scale(1.1)' : 'scale(1)';
              e.currentTarget.style.opacity = '1';
            }
          }}
        >
          {step.isCompleted ? (
            <Check size={19} strokeWidth={2.5} />
          ) : step.isCurrent ? (
            <CircleDot size={18} strokeWidth={3} fill="currentColor" />
          ) : (
            step.stepNumber
          )}
        </div>
      </div>
    );
  };

  const renderStepLabel = (step: StepData) => (
    <span
      className={`mt-2 small text-center ${
        step.isCurrent 
          ? 'text-primary fw-bold' 
          : step.isCompleted 
          ? 'text-success fw-semibold' 
          : 'text-muted'
      } ${step.isClickable ? 'cursor-pointer' : ''}`}
      style={{ 
        fontSize: step.isCurrent ? '11px' : '10px', 
        width: '100%',
        lineHeight: '1.2',
        cursor: step.isClickable ? 'pointer' : 'default',
        overflowWrap: 'anywhere',
        transition: 'all 0.3s ease',
      }}
      title={step.stepLabel}
      onClick={() => handleStepClick(step.stepNumber)}
      role={step.isClickable ? 'button' : undefined}
      tabIndex={step.isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (step.isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          handleStepClick(step.stepNumber);
        }
      }}
    >
      {step.stepLabel}
    </span>
  );

  const renderConnector = (step: StepData) => {
    if (step.stepNumber >= totalSteps) return null;
    
    return (
      <div
        className="mx-2"
        style={{
          width: '26px',
          height: '2px',
          marginTop: '20px',
          flexShrink: 0,
          transition: 'all 0.3s ease',
          backgroundColor: step.isCompleted ? 'var(--bs-success)' : 'var(--bs-light)',
        }}
      />
    );
  };

  const renderStep = (step: StepData, isMobile: boolean) => (
    <React.Fragment key={step.stepNumber}>
      <div 
        ref={(el) => {
          stepRefs.current[step.stepNumber] = el;
        }}
        className="d-flex flex-column align-items-center" 
        style={{ width: isMobile ? '90px' : '116px', flexShrink: 0 }}
      >
        {renderStepCircle(step)}
        {renderStepLabel(step)}
      </div>
      {renderConnector(step)}
    </React.Fragment>
  );

  const containerStyle: React.CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    backgroundColor: 'white',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    marginTop: '-0.75rem',
    marginLeft: '-0.75rem',
    marginRight: '-0.75rem',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 0.6;
              transform: scale(1);
            }
            50% {
              opacity: 0.9;
              transform: scale(1.05);
            }
          }
          
          .desktop-scrollbar-hide {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;  /* Firefox */
          }
          
          .desktop-scrollbar-hide:hover {
            scrollbar-width: thin;  /* Firefox - show on hover */
          }
          
          .desktop-scrollbar-hide::-webkit-scrollbar {
            height: 0px;
            background: transparent;  /* Chrome, Safari and Opera */
            transition: height 1s ease;
          }
          
          .desktop-scrollbar-hide:hover::-webkit-scrollbar {
            height: 8px;
          }
          
          .desktop-scrollbar-hide:hover::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 4px;
            transition: background-color 2s ease;
          }
          
          .desktop-scrollbar-hide:hover::-webkit-scrollbar-thumb:hover {
            background-color: rgba(0, 0, 0, 0.3);
          }
        `}
      </style>
      <div className="mb-3" style={containerStyle}>
        {/* Mobile: Horizontal scrollable */}
        <div 
          ref={mobileScrollRef}
          className="d-lg-none overflow-x-auto" 
          style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}
        >
          <div 
            className="d-flex align-items-start justify-content-center"
            style={{ minWidth: 'max-content', paddingBottom: '8px', margin: '0 auto' }}
          >
            {Array.from({ length: totalSteps }, (_, index) => 
              renderStep(getStepData(index), true)
            )}
          </div>
        </div>

        {/* Desktop: Horizontal scrollable */}
        <div 
          ref={desktopScrollRef}
          className="d-none d-lg-block overflow-x-auto desktop-scrollbar-hide" 
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div 
            className="d-flex align-items-start justify-content-center"
            style={{ minWidth: 'max-content', paddingBottom: '8px', margin: '0 auto' }}
          >
            {Array.from({ length: totalSteps }, (_, index) => 
              renderStep(getStepData(index), false)
            )}
          </div>
        </div>
      </div>
    </>
  );
};

